import { Flex } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useId } from '@/hooks';
import type { FC } from 'react';
import type { z } from 'zod';
import { useBatchFileJson } from '../../../_hooks/batch';
import {
  type BatchModel,
  batchType as batchTypeEnum,
} from '../../../_models/batch';
import { DownloadByURLFile } from './DownloadByURLFile';
import { DownloadCsv } from './DownloadCsv';
import { DownloadPdf } from './DownloadPdf';

type Props = {
  batch: z.infer<typeof BatchModel>;
};

export const DownloadBatchFile: FC<Props> = ({ batch }) => {
  const { id } = useId();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;
  const downloadJson = useBatchFileJson({
    id,
    enabled: batch?.hasDownloadFile && batch.status === 'success',
  });

  const batchType = batchTypeEnum.safeParse(batch.type);

  if (
    !downloadJson.data ||
    !batchType.success ||
    (batchType.data !== 'json-output-for-csv-download' &&
      batchType.data !== 'json-output-for-pdf-download' &&
      batchType.data !== 'csv-bulk-upload' &&
      batchType.data !== 'bulk-print')
  ) {
    return null;
  }

  return (
    <Flex vertical>
      <Flex
        vertical={isDebugMode}
        justify={isDebugMode ? undefined : 'flex-end'}
        style={{
          overflow: isDebugMode ? 'scroll' : undefined,
          width: isDebugMode ? '1000px' : undefined,
        }}
      >
        {batchType.data === 'json-output-for-csv-download' ||
        batchType.data === 'csv-bulk-upload' ? (
          <DownloadCsv
            batch={batch}
            isLoading={downloadJson.isPending}
            jsonData={downloadJson.data}
          />
        ) : batchType.data === 'json-output-for-pdf-download' ? (
          <DownloadPdf
            batch={batch}
            jsonData={downloadJson.data}
            isLoading={downloadJson.isPending}
          />
        ) : (
          <DownloadByURLFile
            batch={batch}
            jsonData={downloadJson.data}
            isLoading={downloadJson.isPending}
          />
        )}
      </Flex>
    </Flex>
  );
};
